import { ButcherHeader } from '@components/ButcherHeader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faEllipsisVertical,
  faSearch,
  faSort,
  faSortUp,
  faSortDown,
} from '@fortawesome/free-solid-svg-icons';
import { SideNav } from '@components/OrderCreation/OrderSideNav';
import Pagination from '@components/Pagination';
import { useEffect, useState, useRef, useCallback } from 'react';
import {
  errorMessage,
  OrderProcessingMasterData,
  pagination,
  PickupCrumbSteps,
  searchConfig,
} from '@helpers/Constants';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { Axios } from '@helpers/ApiService';
import { useToast } from '@contexts/Toast';
import { OrderListBreadcrumbs } from '@components/OrderProcessing/OrderListBreadcrumbs';
import { ProcessingListProps } from '@helpers/Models';
import { OrderStatusBadge } from '@components/OrderStatusBadge';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '@services/OrderCreationService';
import { useDismissDropdown } from '@services/DropdownService';

const pickupStatusOptions = [
  { label: 'Paid', value: 2 },
  { label: 'Picked Up', value: 3 },
];

export const OrderPickupList: React.FC = () => {
  const { showToastMessage } = useToast();
  const navigate = useNavigate();

  // Pagination & search
  const [currentPage, setCurrentPage] = useState(pagination.defaultPage);
  const [itemsPerPage, setItemsPerPage] = useState(pagination.itemsPerPage);
  const [totalPages, setTotalPages] = useState(pagination.defaultPage);
  const [searchKey, setSearchKey] = useState('');
  const [debouncedSearchKey, setDebouncedSearchKey] = useState('');

  // Data
  const [pickups, setPickups] = useState<ProcessingListProps[]>([]);

  // Selection & dropdown
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const dropdownRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const filterDropdownRef = useRef<HTMLDivElement | null>(null);

  // Status filter
  const [selectedOrderStatus, setSelectedOrderStatus] = useState<number | null>(null);
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);

  // Sorting state
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Compute label for filter button
  const selectedOrderStatusLabel =
    pickupStatusOptions.find((status) => status.value === selectedOrderStatus)?.label ??
    'All Pickups';

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchKey(searchKey);
    }, searchConfig.SEARCH_DELAY);
    return () => {
      clearTimeout(handler);
    };
  }, [searchKey]);

  /**
   * Fetch pickups from API with pagination, search, filter, and sorting.
   */
  const fetchPickups = useCallback(
    async (
      page: number,
      searchKeyVal: string,
      count: number = itemsPerPage,
      status: number | null = selectedOrderStatus,
      field: string = sortField,
      direction: 'asc' | 'desc' = sortDirection,
    ): Promise<void> => {
      try {
        const payload: {
          page: number;
          count: number;
          searchKey: string;
          status?: number;
          sort?: Record<string, 'asc' | 'desc'>; // <-- Use object format
        } = {
          page,
          count,
          searchKey: searchKeyVal,
        };
        if (status !== null) {
          payload.status = status;
        }
        if (field) {
          payload.sort = { [field]: direction }; // <-- Same as InvoiceList
        }

        const response = await Axios.post(ApiEndpoints.PICKUP_LIST, payload);
        const data = response?.data?.data;
        const orders = (data?.orders ?? data?.items ?? []) as ProcessingListProps[];

        // Directly store API response array – maintains server sort order
        setPickups(orders);
        setCurrentPage(data?.currentPage ?? page);
        setTotalPages(data?.totalPages ?? 1);
      } catch (error) {
        showToastMessage(errorMessage.FETCH_ERROR, 'error');
      }
    },
    [itemsPerPage, selectedOrderStatus, sortField, sortDirection, showToastMessage],
  );

  // Fetch data when dependencies change
  useEffect(() => {
    fetchPickups(
      currentPage,
      debouncedSearchKey,
      itemsPerPage,
      selectedOrderStatus,
      sortField,
      sortDirection,
    );
  }, [
    currentPage,
    itemsPerPage,
    debouncedSearchKey,
    selectedOrderStatus,
    sortField,
    sortDirection,
    fetchPickups,
  ]);

  // Close dropdowns on outside click
  useDismissDropdown({
    refs: [
      ...Array.from(dropdownRefs.current.values()).map((el) => ({
        current: el,
      })),
      { current: filterDropdownRef.current },
    ],
    onClose: () => {
      setActiveDropdown(null);
      setIsFilterDropdownOpen(false);
    },
  });

  const toggleDropdown = (orderId: number) => {
    setActiveDropdown(activeDropdown === orderId ? null : orderId);
  };

  const handleStatusFilterSelect = (status: number | null): void => {
    setSelectedOrderStatus(status);
    setCurrentPage(1);
    setIsFilterDropdownOpen(false);
  };

  // Navigation handlers
  const handleViewAllInvoices = (orderId: number): void => {
    const selectedPickup = pickups.find((data) => data.id === orderId);
    const orderInfoDetails = {
      orderId: selectedPickup?.id,
      orderNumber: selectedPickup?.orderNumber,
      customerName: selectedPickup?.customerName,
      roles: selectedPickup?.roles,
      createdBy: selectedPickup?.createdUserName,
      createdDate: selectedPickup?.createdAt,
      fromPickup: true,
    };
    setSelectedOrderId(orderId);
    navigate(FrontendRoutes.ALL_INVOICES, { state: orderInfoDetails });
  };

  const handlePickupRowClick = async (pickup: ProcessingListProps): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.ORDER_INVOICE_VIEW, {
        id: pickup.id,
      });

      const invoiceDetail = response?.data?.data;
      const invoiceId = invoiceDetail?.invoiceInfo?.invoiceId;
      if (!invoiceId) {
        showToastMessage(errorMessage.INVOICE_NOT_FOUND, 'error');
        return;
      }

      setSelectedOrderId(pickup.id);
      navigate(FrontendRoutes.INVOICE_DETAILS, {
        state: {
          orderId: invoiceDetail?.orderInfo?.orderId ?? pickup.id,
          orderNumber: invoiceDetail?.orderInfo?.orderNumber ?? pickup.orderNumber,
          customerName: invoiceDetail?.orderInfo?.customerName ?? pickup.customerName,
          roles: invoiceDetail?.orderInfo?.roles ?? pickup.roles,
          createdBy: invoiceDetail?.orderInfo?.createdByUser ?? pickup.createdUserName,
          createdDate: invoiceDetail?.orderInfo?.orderDate ?? pickup.createdAt,
          customerEmail: invoiceDetail?.orderInfo?.customerEmail,
          customerPhone: invoiceDetail?.orderInfo?.customerPhone,
          invoiceId,
          fromPickup: true,
        },
      });
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  const handleViewOrderSummary = (orderId: number) => {
    navigate(FrontendRoutes.ORDER_DETAILS, { state: { orderId } });
  };

  const handleViewServiceProcessing = (orderId: number) => {
    navigate(FrontendRoutes.SERVICE_PROCESS_TASK_LIST, { state: { orderId } });
  };

  const handleViewProductProcessing = (orderId: number) => {
    navigate(FrontendRoutes.PRODUCT_PROCESSING_TASK_LIST, { state: { orderId } });
  };

  // Sorting helpers
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const renderSortIcon = (field: string) => {
    if (sortField !== field) {
      return <FontAwesomeIcon icon={faSort} className="ml-1 text-gray-300" />;
    }
    if (sortDirection === 'asc') {
      return <FontAwesomeIcon icon={faSortUp} className="ml-1 text-gray-600" />;
    }
    return <FontAwesomeIcon icon={faSortDown} className="ml-1 text-gray-600" />;
  };

  const BreadCrumbsProps = {
    steps: PickupCrumbSteps,
    activeItem: 'Pick Up',
    type: OrderProcessingMasterData.PICKUP,
  };

  return (
    <div className="flex h-screen w-screen gap-1">
      <SideNav active={7} />
      <div className="flex-1 p-1 overflow-auto relative">
        <ButcherHeader />
        <OrderListBreadcrumbs orderInfo={BreadCrumbsProps} />
        <div className="flex justify-between items-center p-3">
          <h1 className="font-semibold text-xl px-5 mt-3">Pick Up</h1>
          <div className="flex items-center gap-2">
            <div className="relative" ref={filterDropdownRef}>
              <button
                type="button"
                onClick={() =>
                  setIsFilterDropdownOpen((previousState) => !previousState)
                }
                className="min-w-[180px] h-10 border border-gray-300 bg-white px-4 rounded text-left text-sm font-medium text-[#1C1B1F] flex items-center justify-between gap-3 hover:border-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                <span>{selectedOrderStatusLabel}</span>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={`text-gray-500 transition-transform duration-200 ${isFilterDropdownOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {isFilterDropdownOpen && (
                <div className="absolute left-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                  <ul className="py-2">
                    <li className="mx-2 my-0">
                      <button
                        type="button"
                        onClick={() => handleStatusFilterSelect(null)}
                        className="w-full rounded-md px-4 py-2 text-left text-sm font-semibold text-[#1C1B1F] hover:bg-primary hover:text-white"
                      >
                        All Pickups
                      </button>
                    </li>
                    {pickupStatusOptions.map((status) => (
                      <li key={status.value} className="mx-2 my-0">
                        <button
                          type="button"
                          onClick={() => handleStatusFilterSelect(status.value)}
                          className="w-full rounded-md px-4 py-2 text-left text-sm font-semibold text-[#1C1B1F] hover:bg-primary hover:text-white"
                        >
                          {status.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="relative">
              <input
                type="text"
                placeholder="Search..."
                onChange={(e) => setSearchKey(e.target.value)}
                value={searchKey}
                className="border p-2 pl-3 pr-10 w-80 accent-primary focus:outline-primary border-gray-300 rounded"
              />
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
              />
            </div>
          </div>
        </div>
        <div className="shadow-lg mt-3 rounded-lg">
          <form onSubmit={(e) => e.preventDefault()}>
            <table className="w-full table-auto rounded-xl">
              <thead className="bg-gray-100">
                <tr className="border-b">
                  <th className="px-4 py-2 text-center font-semibold align-middle">
                    <button
                      type="button"
                      onClick={() => handleSort('invoiceNumber')}
                      className="flex items-center justify-center gap-1 w-full focus:outline-none"
                    >
                      Invoice No.
                      {renderSortIcon('invoiceNumber')}
                    </button>
                  </th>
                  <th className="px-4 py-2 text-center font-semibold align-middle">
                    <button
                      type="button"
                      onClick={() => handleSort('orderNumber')}
                      className="flex items-center justify-center gap-1 w-full focus:outline-none"
                    >
                      Order No.
                      {renderSortIcon('orderNumber')}
                    </button>
                  </th>
                  <th className="px-4 py-2 text-center font-semibold align-middle">
                    <button
                      type="button"
                      onClick={() => handleSort('customerName')}
                      className="flex items-center justify-center gap-1 w-full focus:outline-none"
                    >
                      Customer
                      {renderSortIcon('customerName')}
                    </button>
                  </th>
                  <th className="px-4 py-2 text-center font-semibold align-middle">
                    <button
                      type="button"
                      onClick={() => handleSort('speciesName')}
                      className="flex items-center justify-center gap-1 w-full focus:outline-none"
                    >
                      Species Name
                      {renderSortIcon('speciesName')}
                    </button>
                  </th>
                  <th className="px-4 py-2 text-center font-semibold align-middle">
                    <button
                      type="button"
                      onClick={() => handleSort('invoiceDate')}
                      className="flex items-center justify-center gap-1 w-full focus:outline-none"
                    >
                      Invoice Date
                      {renderSortIcon('invoiceDate')}
                    </button>
                  </th>
                  <th className="px-4 py-2 text-center font-semibold align-middle">
                    <button
                      type="button"
                      onClick={() => handleSort('status')}
                      className="flex items-center justify-center gap-1 w-full focus:outline-none"
                    >
                      Pickup Status
                      {renderSortIcon('status')}
                    </button>
                  </th>
                  <th className="px-4 py-2 text-center font-semibold align-middle">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {pickups.length > 0 ? (
                  pickups.map((order, index) => {
                    const rowKey = `pickup-row-${index}-${order.id ?? ''}-${order.orderNumber ?? ''}`;
                    return (
                      <tr
                        onClick={() => {
                          void handlePickupRowClick(order);
                        }}
                        key={rowKey}
                        className={`hover:cursor-pointer hover:bg-tertiary hover:border hover:border-primary hover:b-2 ${selectedOrderId === order.id
                            ? 'bg-tertiary border border-primary'
                            : ''
                          }`}
                      >
                        <td className="px-4 py-2 text-center whitespace-nowrap align-middle">
                          {order.invoiceNumber || '-'}
                        </td>
                        <td className="px-4 py-2 text-center whitespace-nowrap align-middle">
                          {order.orderNumber}
                        </td>
                        <td className="px-4 py-2 text-center whitespace-nowrap align-middle">
                          {order.customerName}
                        </td>
                        <td className="px-4 py-2 text-center whitespace-nowrap align-middle">
                          {order.speciesName || '-'}
                        </td>
                        <td className="px-4 py-2 text-center whitespace-nowrap align-middle">
                          {formatDate(String(order.invoiceDate || ''))}
                        </td>
                        <td className="px-4 py-2 text-center whitespace-nowrap align-middle">
                          <div className="flex justify-center items-center">
                            <OrderStatusBadge
                              type="pickupStatus"
                              status={Number(order.status)}
                            />
                          </div>
                        </td>
                        <td className="px-4 py-2 text-center relative align-middle">
                          <button
                            type="button"
                            className="p-4"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDropdown(order.id);
                            }}
                          >
                            <FontAwesomeIcon icon={faEllipsisVertical} />
                          </button>

                          {activeDropdown === order.id && (
                            <div
                              className="absolute right-0 mt-2 w-56 bg-white border rounded-lg shadow-lg z-10"
                              ref={(el) => {
                                if (el) {
                                  dropdownRefs.current.set(order.id, el);
                                } else {
                                  dropdownRefs.current.delete(order.id);
                                }
                              }}
                            >
                              <ul className="py-2 text-left">
                                <li className="mx-2 my-0">
                                  <button
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleViewOrderSummary(order.id);
                                    }}
                                    type="button"
                                    className="w-full px-4 py-2 text-sm font-semibold hover:bg-[#EFF1F5] cursor-pointer rounded-md text-left"
                                  >
                                    View order summary
                                  </button>
                                </li>
                                <li className="mx-2 my-0">
                                  <button
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleViewAllInvoices(order.id);
                                    }}
                                    type="button"
                                    className="w-full px-4 py-2 text-sm font-semibold hover:bg-[#EFF1F5] cursor-pointer rounded-md text-left"
                                  >
                                    View invoices
                                  </button>
                                </li>
                                <li className="mx-2 my-0">
                                  <button
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleViewServiceProcessing(order.id);
                                    }}
                                    type="button"
                                    className="w-full px-4 py-2 text-sm font-semibold hover:bg-[#EFF1F5] cursor-pointer rounded-md text-left"
                                  >
                                    View service processing
                                  </button>
                                </li>
                                <li className="mx-2 my-0">
                                  <button
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleViewProductProcessing(order.id);
                                    }}
                                    type="button"
                                    className="w-full px-4 py-2 text-sm font-semibold hover:bg-[#EFF1F5] cursor-pointer rounded-md text-left"
                                  >
                                    View product processing
                                  </button>
                                </li>
                              </ul>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={7} className="px-4 py-2 text-center align-middle">
                      No data available.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </form>

          <div className="bottom-0">
            <Pagination
              totalPages={totalPages}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          </div>
        </div>
      </div>
    </div>
  );
};